// ==================== LEGAL PAGES ====================
import { Shield, Cookie, Robot, Warning, FileText, Scales, Gavel } from "@phosphor-icons/react";

const LegalPageLayout = ({ title, children }) => {
  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      <header className="sticky top-0 z-50 bg-[#F4F4F0]/90 backdrop-blur-xl border-b border-[#171717] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <Scales size={32} weight="regular" className="text-[#8B1A1A]" />
            <span className="font-serif text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tecnoabogado
            </span>
          </a>
          <nav className="flex items-center gap-4 text-sm">
            <a href="/privacidad" className="text-[#525252] hover:text-[#0A0A0A]">Privacidad</a>
            <a href="/cookies" className="text-[#525252] hover:text-[#0A0A0A]">Cookies</a>
            <a href="/terminos" className="text-[#525252] hover:text-[#0A0A0A]">Terminos</a>
            <a href="/uso-ia" className="text-[#525252] hover:text-[#0A0A0A]">IA</a>
            <a href="/aviso-legal" className="text-[#525252] hover:text-[#0A0A0A]">Aviso legal</a>
          </nav>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          {title}
        </h1>
        <div className="prose prose-lg max-w-none" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {children}
        </div>
      </main>
      
      <footer className="border-t border-[#171717] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-[#525252]">
          <p className="mb-2">© {new Date().getFullYear()} Jose Luis Vilaplana (Tecnoabogado). Todos los derechos reservados.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://www.iabogacia.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#0A0A0A] transition-colors">www.iabogacia.com</a>
            <a href="https://www.tecnoabogado.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#0A0A0A] transition-colors">www.tecnoabogado.com</a>
            <a href="mailto:consultas@tecnoabogado.com" className="hover:text-[#0A0A0A] transition-colors">consultas@tecnoabogado.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== POLITICA DE PRIVACIDAD ====================
export const PrivacyPage = () => (
  <LegalPageLayout title="Politica de privacidad">
    <div className="space-y-8 text-[#525252]">
      <p className="text-sm text-[#A3A3A3]">Ultima actualizacion: Abril 2026</p>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Shield size={24} className="text-[#8B1A1A]" />
          1. Informacion que recopilamos
        </h2>
        <p className="mb-4">Recopilamos informacion que nos proporcionas directamente cuando utilizas nuestra calculadora de honorarios:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Informacion de cuenta:</strong> Nombre, correo electronico, metodo de inicio de sesion (OAuth con Google)</li>
          <li><strong>Interacciones con la IA:</strong> Descripciones de casos, calculos realizados, justificaciones generadas</li>
          <li><strong>Preferencias:</strong> Consentimiento newsletter, preferencias de baremo</li>
          <li><strong>Datos tecnicos:</strong> Direccion IP, navegador, dispositivo, cookies</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          2. Como utilizamos tu informacion
        </h2>
        <p className="mb-4">Utilizamos la informacion recopilada para:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Proporcionar y mejorar el servicio de calculo de honorarios</li>
          <li>Generar justificaciones mediante inteligencia artificial (GPT-5.2)</li>
          <li>Enviar comunicaciones relevantes sobre actualizaciones (con tu consentimiento)</li>
          <li><strong>Comunicaciones comerciales:</strong> Informarte sobre otros productos, servicios y herramientas de Tecnoabogado que puedan ser de tu interes profesional (con tu consentimiento previo)</li>
          <li>Cumplir con obligaciones legales (EU AI Act, RGPD, AEPD)</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          3. Base legal para el procesamiento (RGPD)
        </h2>
        <p className="mb-4">Procesamos tus datos personales bajo las siguientes bases legales:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Consentimiento (Art. 6(1)(a) RGPD):</strong> Para newsletter, comunicaciones comerciales y ofertas de productos o servicios relacionados</li>
          <li><strong>Ejecucion de contrato (Art. 6(1)(b) RGPD):</strong> Para proporcionar el servicio de la plataforma</li>
          <li><strong>Interes legitimo (Art. 6(1)(f) RGPD):</strong> Para mejorar la seguridad y prevenir fraude</li>
          <li><strong>Obligacion legal (Art. 6(1)(c) RGPD):</strong> Para cumplir con EU AI Act y AEPD</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          4. Compartir informacion con terceros
        </h2>
        <p className="mb-4">Compartimos informacion con los siguientes proveedores de servicios:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>OpenAI (GPT-5.2):</strong> Proveedor de IA para generacion de justificaciones</li>
          <li><strong>Google OAuth:</strong> Servicio de autenticacion</li>
          <li><strong>MongoDB:</strong> Almacenamiento de datos</li>
        </ul>
        <p className="mt-4 font-semibold">NO vendemos tus datos personales a terceros bajo ninguna circunstancia.</p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          5. Retencion de datos
        </h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Datos de cuenta:</strong> Mientras tu cuenta este activa</li>
          <li><strong>Calculos realizados:</strong> No se almacenan permanentemente (tiempo real)</li>
          <li><strong>Logs de auditoria:</strong> 1 ano (cumplimiento legal)</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          6. Tus derechos (RGPD)
        </h2>
        <p className="mb-4">Tienes los siguientes derechos sobre tus datos personales:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Derecho de acceso (Art. 15):</strong> Solicitar una copia de tus datos</li>
          <li><strong>Derecho de rectificacion (Art. 16):</strong> Corregir datos inexactos</li>
          <li><strong>Derecho al olvido (Art. 17):</strong> Solicitar la eliminacion de tus datos</li>
          <li><strong>Derecho a la portabilidad (Art. 20):</strong> Recibir tus datos en formato estructurado</li>
          <li><strong>Derecho de oposicion (Art. 21):</strong> Oponerte al procesamiento de tus datos</li>
          <li><strong>Derecho a retirar consentimiento:</strong> En cualquier momento</li>
        </ul>
        <p className="mt-4">Para ejercer estos derechos, contacta a: <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a></p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          7. Cumplimiento AEPD (Espana)
        </h2>
        <p className="mb-4">
          Estamos comprometidos con el cumplimiento de la normativa de la Agencia Espanola de Proteccion de Datos (AEPD). 
          En caso de brecha de datos, notificaremos a la AEPD en menos de 72 horas.
        </p>
        <p>Mas informacion: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[#8B1A1A] hover:underline">www.aepd.es</a></p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          8. Contacto
        </h2>
        <p>Para preguntas sobre privacidad o ejercer tus derechos:</p>
        <ul className="list-none pl-0 mt-4 space-y-1">
          <li><strong>Responsable:</strong> Jose Luis Vilaplana (@tecnoabogado)</li>
          <li><strong>Email:</strong> <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a></li>
          <li><strong>Autoridad de control:</strong> Agencia Espanola de Proteccion de Datos (AEPD)</li>
        </ul>
      </section>
    </div>
  </LegalPageLayout>
);

// ==================== POLITICA DE COOKIES ====================
export const CookiesPage = () => (
  <LegalPageLayout title="Politica de cookies">
    <div className="space-y-8 text-[#525252]">
      <p className="text-sm text-[#A3A3A3]">Ultima actualizacion: Abril 2026</p>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Cookie size={24} className="text-[#8B1A1A]" />
          Que son las cookies?
        </h2>
        <p>
          Las cookies son pequenos archivos de texto que se almacenan en tu dispositivo (ordenador, tablet, smartphone) cuando visitas un sitio web. 
          Permiten que el sitio web recuerde tus acciones y preferencias durante un periodo de tiempo, mejorando tu experiencia de navegacion.
        </p>
        <p className="mt-4">
          En cumplimiento de la Ley 34/2002 de Servicios de la Sociedad de la Informacion y Comercio Electronico (LSSI), 
          el Reglamento General de Proteccion de Datos (RGPD UE 2016/679), y las directrices de la Agencia Espanola de Proteccion de Datos (AEPD), 
          te informamos sobre las cookies que utiliza este sitio web.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Tipos de cookies que utilizamos
        </h2>
        
        <div className="bg-white border border-[#171717] rounded-none p-6 mb-6">
          <h3 className="font-semibold text-[#0A0A0A] mb-4">1. Cookies tecnicas (necesarias)</h3>
          <p className="mb-4 text-sm">Son imprescindibles para el funcionamiento del sitio web. No requieren consentimiento previo segun el Art. 22.2 LSSI.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F4F0]">
                <tr>
                  <th className="p-2 text-left border">Cookie</th>
                  <th className="p-2 text-left border">Finalidad</th>
                  <th className="p-2 text-left border">Duracion</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-mono">session_token</td>
                  <td className="p-2 border">Autenticacion de usuario (OAuth)</td>
                  <td className="p-2 border">7 dias</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white border border-[#171717] rounded-none p-6">
          <h3 className="font-semibold text-[#0A0A0A] mb-4">2. Cookies de terceros</h3>
          <p className="text-sm">
            Este sitio web NO utiliza cookies publicitarias ni de redes sociales. 
            Solo utilizamos cookies de terceros para servicios esenciales de autenticacion (Google OAuth).
            Los datos de sesion y la informacion de tu cuenta (incluido el correo electronico) pueden ser utilizados para enviarte comunicaciones comerciales sobre productos y servicios de Tecnoabogado, siempre con tu consentimiento previo.
          </p>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Como gestionar las cookies
        </h2>
        <p className="mb-4">Puedes permitir, bloquear o eliminar las cookies instaladas en tu dispositivo mediante la configuracion de tu navegador:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Google Chrome:</strong> Configuracion → Privacidad y seguridad → Cookies</li>
          <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
          <li><strong>Safari:</strong> Preferencias → Privacidad → Gestionar datos de sitios web</li>
          <li><strong>Microsoft Edge:</strong> Configuracion → Cookies y permisos del sitio</li>
        </ul>
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mt-4">
          <p className="text-sm text-[#92400E]">
            <strong>Importante:</strong> Si deshabilitas las cookies tecnicas, algunas funcionalidades del sitio web 
            (como el inicio de sesion) podrian no funcionar correctamente.
          </p>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Base legal y derechos
        </h2>
        <p className="mb-4">El tratamiento de datos personales a traves de cookies se basa en:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Interes legitimo (Art. 6.1.f RGPD):</strong> Para cookies tecnicas necesarias</li>
        </ul>
        <p className="mt-4"><strong>Referencias normativas:</strong></p>
        <ul className="list-disc pl-6 space-y-2">
          <li>LSSI Art. 22 - Obligacion de informar sobre cookies</li>
          <li>AEPD - Directrices sobre uso de cookies y consentimiento</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Contacto
        </h2>
        <ul className="list-none pl-0 space-y-1">
          <li><strong>Responsable:</strong> Jose Luis Vilaplana</li>
          <li><strong>Email:</strong> <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a></li>
        </ul>
        <p className="mt-4">
          Tienes derecho a presentar una reclamacion ante la Agencia Espanola de Proteccion de Datos (AEPD): 
          <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[#8B1A1A] hover:underline ml-1">www.aepd.es</a>
        </p>
      </section>
    </div>
  </LegalPageLayout>
);

// ==================== TERMINOS DE USO ====================
export const TermsPage = () => (
  <LegalPageLayout title="Terminos de uso">
    <div className="space-y-8 text-[#525252]">
      <p className="text-sm text-[#A3A3A3]">Ultima actualizacion: Abril 2026</p>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <FileText size={24} className="text-[#8B1A1A]" />
          1. Aceptacion de los terminos
        </h2>
        <p>
          Al acceder y utilizar la plataforma "Calculadora de Honorarios Tecnoabogado" (en adelante, "la Plataforma"), 
          aceptas estar sujeto a estos Terminos de Uso y a nuestra Politica de Privacidad. Si no estas de acuerdo, no utilices la Plataforma.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          2. Descripcion del servicio
        </h2>
        <p className="mb-4">La Plataforma ofrece:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Herramienta de calculo orientativo de honorarios profesionales</li>
          <li>Estimacion de horas de dedicacion por tipo de procedimiento</li>
          <li>Generacion de justificaciones mediante IA (GPT-5.2)</li>
          <li>Exportacion de presupuestos en formato PDF</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          3. Naturaleza orientativa - Disclaimer
        </h2>
        <div className="bg-[#FEE2E2] border-l-4 border-[#DC2626] p-4 mb-4">
          <p className="font-semibold text-[#991B1B] mb-2">MUY IMPORTANTE</p>
          <p className="text-[#991B1B]">
            Los honorarios profesionales de la abogacia son LIBRES. Esta herramienta es una meta-ayuda orientativa 
            y NO puede considerarse como minimo ni como orientador obligatorio.
          </p>
        </div>
        <p className="mb-4">Debes tener en cuenta:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>No es vinculante:</strong> Los calculos NO constituyen un presupuesto vinculante ni una oferta de servicios</li>
          <li><strong>Puede contener imprecisiones:</strong> Las estimaciones pueden variar significativamente del caso real</li>
          <li><strong>El profesional decide:</strong> El abogado o abogada decidira sus honorarios segun sus costes, experiencia, especialidad y criterio</li>
          <li><strong>Verifica siempre:</strong> Consulta fuentes oficiales y utiliza tu criterio profesional</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          4. Uso del chatbot IA - Limitaciones
        </h2>
        <p className="mb-4">El chatbot IA es un sistema automatizado que genera respuestas basadas en modelos de lenguaje. Debes tener en cuenta:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>No es asesoramiento profesional:</strong> Las respuestas NO constituyen asesoramiento legal</li>
          <li><strong>Puede contener errores:</strong> La IA puede generar informacion inexacta o incompleta</li>
          <li><strong>Sesgo potencial:</strong> Los modelos IA pueden reflejar sesgos presentes en sus datos de entrenamiento</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          5. Cuenta de usuario
        </h2>
        <p className="mb-4">Para utilizar la Plataforma, debes crear una cuenta. Te comprometes a:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Proporcionar informacion precisa y actualizada</li>
          <li>Mantener la seguridad de tu cuenta</li>
          <li>Notificarnos inmediatamente de cualquier uso no autorizado</li>
          <li>No compartir tu cuenta con terceros</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          6. Uso aceptable
        </h2>
        <p className="mb-4">Te comprometes a NO:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Usar la Plataforma para fines ilegales o no autorizados</li>
          <li>Intentar acceder a sistemas o datos no autorizados</li>
          <li>Realizar ingenieria inversa del sistema</li>
          <li>Enviar spam, malware o contenido malicioso</li>
          <li>Usar bots o scripts automatizados sin autorizacion</li>
          <li>Extraer datos masivamente (scraping) sin permiso</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          7. Propiedad intelectual
        </h2>
        <p className="mb-4">
          Todo el contenido de la Plataforma (textos, graficos, codigo, diseno) es propiedad de Jose Luis Vilaplana (Tecnoabogado) 
          o sus licenciantes, y esta protegido por leyes de propiedad intelectual.
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Licencia de uso:</strong> Te otorgamos una licencia limitada, no exclusiva, intransferible y revocable para usar la Plataforma con fines profesionales</li>
          <li><strong>Contenido generado por IA:</strong> Las respuestas generadas estan sujetas a los terminos del proveedor de IA</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          8. Cumplimiento legal (EU AI Act + RGPD)
        </h2>
        <p className="mb-4">La Plataforma cumple con el EU AI Act (Reglamento (UE) 2024/1689) y el RGPD (Reglamento (UE) 2016/679):</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Transparencia (Art. 50 EU AI Act):</strong> Informamos claramente que se utiliza IA</li>
          <li><strong>Consentimiento granular (RGPD):</strong> Solicitamos consentimiento especifico para cada uso de datos</li>
          <li><strong>Logs auditables:</strong> Registramos interacciones IA para cumplimiento</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          9. Limitacion de responsabilidad
        </h2>
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mb-4">
          <p className="text-[#92400E]">
            LA PLATAFORMA SE PROPORCIONA "TAL CUAL" SIN GARANTIAS DE NINGUN TIPO. 
            No garantizamos disponibilidad ininterrumpida, exactitud del contenido IA, ni ausencia de errores.
          </p>
        </div>
        <p>
          En la maxima medida permitida por la ley, no seremos responsables de danos indirectos, incidentales, 
          especiales o consecuentes derivados del uso de la Plataforma.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          10. Ley aplicable y jurisdiccion
        </h2>
        <p>
          Estos Terminos se rigen por las leyes de Espana y la Union Europea. 
          Cualquier disputa se resolvera en los tribunales correspondientes al domicilio del titular.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          11. Contacto
        </h2>
        <p>Para preguntas sobre estos Terminos:</p>
        <ul className="list-none pl-0 mt-4 space-y-1">
          <li><strong>Responsable:</strong> Jose Luis Vilaplana (@tecnoabogado)</li>
          <li><strong>Email:</strong> <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a></li>
        </ul>
      </section>
    </div>
  </LegalPageLayout>
);

// ==================== TRANSPARENCIA IA (EU AI ACT) ====================
export const AIUsagePage = () => (
  <LegalPageLayout title="Transparencia IA (EU AI Act)">
    <div className="space-y-8 text-[#525252]">
      <p className="text-sm text-[#A3A3A3]">Ultima actualizacion: Abril 2026</p>
      
      <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mb-6">
        <p className="font-semibold text-[#92400E] mb-2">Aviso obligatorio (EU AI Act Art. 50)</p>
        <p className="text-[#92400E]">
          Esta plataforma utiliza sistemas de inteligencia artificial para generar contenido y justificaciones. 
          Segun el Reglamento (UE) 2024/1689 (EU AI Act), debemos informarte claramente sobre el uso de IA y sus limitaciones.
        </p>
      </div>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Robot size={24} className="text-[#8B1A1A]" />
          1. Sistemas de IA utilizados
        </h2>
        <p className="mb-4">La plataforma utiliza los siguientes modelos de IA:</p>
        <div className="bg-white border border-[#171717] rounded-none p-6 mb-4">
          <p><strong>GPT-5.2 (OpenAI):</strong> Modelo de lenguaje para generacion de justificaciones profesionales de honorarios</p>
        </div>
        <p><strong>Clasificacion de riesgo:</strong> Riesgo Medio segun EU AI Act (Art. 50) - Sistema de IA conversacional para uso profesional</p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          2. Capacidades y limitaciones
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-green-50 border border-green-200 rounded-none p-6">
            <h3 className="font-semibold text-green-800 mb-4">Puede hacer:</h3>
            <ul className="list-disc pl-6 space-y-2 text-green-700 text-sm">
              <li>Generar justificaciones orientativas de honorarios</li>
              <li>Analizar descripciones de casos legales</li>
              <li>Proporcionar informacion sobre fases procesales</li>
              <li>Estimar tiempos de dedicacion</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-none p-6">
            <h3 className="font-semibold text-red-800 mb-4">NO puede hacer:</h3>
            <ul className="list-disc pl-6 space-y-2 text-red-700 text-sm">
              <li>Proporcionar asesoramiento legal vinculante</li>
              <li>Garantizar la exactitud del 100% de la informacion</li>
              <li>Reemplazar el criterio profesional del abogado</li>
              <li>Establecer honorarios obligatorios o minimos</li>
            </ul>
          </div>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          3. Riesgos y consideraciones
        </h2>
        
        <div className="space-y-4">
          <div className="border-l-4 border-[#8B1A1A] pl-4">
            <h3 className="font-semibold text-[#0A0A0A]">Inexactitudes ("Alucinaciones")</h3>
            <p className="text-sm">
              Los modelos de IA pueden generar informacion que suena convincente pero es incorrecta o inventada. 
              Siempre verifica informacion critica con tu criterio profesional.
            </p>
          </div>
          
          <div className="border-l-4 border-[#8B1A1A] pl-4">
            <h3 className="font-semibold text-[#0A0A0A]">Sesgo potencial</h3>
            <p className="text-sm">
              Los modelos IA se entrenan con datos que pueden contener sesgos. 
              No podemos garantizar ausencia total de sesgo en las respuestas.
            </p>
          </div>
          
          <div className="border-l-4 border-[#8B1A1A] pl-4">
            <h3 className="font-semibold text-[#0A0A0A]">Informacion desactualizada</h3>
            <p className="text-sm">
              Los modelos tienen un "corte de conocimiento" y pueden no conocer cambios normativos recientes.
            </p>
          </div>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          4. Uso responsable
        </h2>
        <p className="mb-4">Para usar la IA de forma responsable:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Verifica la informacion:</strong> Consulta fuentes oficiales y utiliza tu criterio profesional</li>
          <li><strong>No compartas informacion sensible:</strong> Evita datos personales de clientes en las descripciones</li>
          <li><strong>Usa con sentido critico:</strong> No aceptes respuestas sin cuestionarlas</li>
          <li><strong>Reporta problemas:</strong> Si detectas sesgo o informacion incorrecta, contactanos</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          5. Cumplimiento legal
        </h2>
        
        <div className="bg-white border border-[#171717] rounded-none p-6 mb-4">
          <h3 className="font-semibold text-[#0A0A0A] mb-4">EU AI Act (Reglamento (UE) 2024/1689)</h3>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li><strong>Art. 50 - Transparencia:</strong> Informamos que se utiliza IA</li>
            <li><strong>Arts. 10, 26, 29 - Sesgo:</strong> Monitoreamos el comportamiento del sistema</li>
            <li><strong>Arts. 11, 13 - Documentacion:</strong> Documentamos modelo y capacidades</li>
          </ul>
        </div>
        
        <div className="bg-white border border-[#171717] rounded-none p-6">
          <h3 className="font-semibold text-[#0A0A0A] mb-4">RGPD (Reglamento (UE) 2016/679)</h3>
          <ul className="list-disc pl-6 space-y-2 text-sm">
            <li>Consentimiento granular para procesamiento de datos</li>
            <li>Logs auditables de interacciones IA</li>
          </ul>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          6. Reportar problemas
        </h2>
        <p className="mb-4">Si encuentras:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Informacion incorrecta o peligrosa</li>
          <li>Sesgo o discriminacion</li>
          <li>Contenido inapropiado</li>
          <li>Violacion de privacidad</li>
        </ul>
        <p className="mt-4">Por favor, reportalo a: <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a></p>
      </section>
    </div>
  </LegalPageLayout>
);

// ==================== AVISO LEGAL ====================
export const DisclaimerPage = () => (
  <LegalPageLayout title="Aviso legal y exencion de responsabilidad">
    <div className="space-y-8 text-[#525252]">
      <p className="text-sm text-[#A3A3A3]">Ultima actualizacion: Abril 2026</p>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Gavel size={24} className="text-[#8B1A1A]" />
          1. Identificacion del titular
        </h2>
        <div className="bg-white border border-[#171717] rounded-none p-6">
          <ul className="list-none space-y-2">
            <li><strong>Titular:</strong> Jose Luis Vilaplana</li>
            <li><strong>Nombre comercial:</strong> Tecnoabogado</li>
            <li><strong>Webs:</strong> www.iabogacia.com · www.tecnoabogado.com</li>
            <li><strong>Contacto:</strong> consultas@tecnoabogado.com</li>
          </ul>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          2. Libertad de honorarios profesionales
        </h2>
        <div className="bg-[#FEE2E2] border-l-4 border-[#DC2626] p-4 mb-4">
          <p className="font-semibold text-[#991B1B] mb-2">Principio fundamental</p>
          <p className="text-[#991B1B]">
            Los honorarios profesionales de la abogacia son LIBRES. Cada profesional tiene plena libertad para fijar sus honorarios conforme a su criterio.
          </p>
        </div>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          3. Naturaleza de esta herramienta
        </h2>
        <p className="mb-4">
          Esta herramienta constituye una <strong>meta-ayuda orientativa</strong> para que el profesional del derecho pueda valorar diferentes criterios a la hora de establecer sus honorarios. En ningun caso:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Puede considerarse como un <strong>minimo</strong> de honorarios</li>
          <li>Puede considerarse como un <strong>orientador obligatorio</strong></li>
          <li>Sustituye el criterio profesional del abogado o abogada</li>
          <li>Vincula a las partes en ninguna relacion juridica</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          4. Factores para determinar honorarios
        </h2>
        <p className="mb-4">El abogado o abogada decidira sus honorarios considerando, entre otros factores:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Sus costes de estructura y operativos</li>
          <li>Su experiencia profesional</li>
          <li>Su especialidad y formacion</li>
          <li>La complejidad del asunto concreto</li>
          <li>El tiempo y dedicacion previstos</li>
          <li>La situacion del cliente</li>
          <li>Cualquier otro factor que considere oportuno</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          5. Recopilacion de datos y comunicaciones comerciales
        </h2>
        <p className="mb-4">
          Al acceder a la Plataforma mediante Google OAuth, se recopila tu correo electronico y datos basicos de perfil. Estos datos se almacenan de forma segura y pueden ser utilizados para:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Gestionar tu cuenta y acceso a la herramienta</li>
          <li>Enviarte comunicaciones comerciales sobre productos, servicios y herramientas de Tecnoabogado que puedan resultar de interes profesional</li>
          <li>Informarte de actualizaciones y mejoras de la plataforma</li>
        </ul>
        <p className="mt-4">
          Las comunicaciones comerciales se realizaran siempre con tu consentimiento previo, conforme al Art. 21 de la LSSI y el Art. 6(1)(a) del RGPD. 
          Puedes revocar tu consentimiento en cualquier momento escribiendo a <a href="mailto:consultas@tecnoabogado.com" className="text-[#8B1A1A] hover:underline">consultas@tecnoabogado.com</a>.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          6. Exencion de responsabilidad
        </h2>
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mb-4">
          <p className="font-semibold text-[#92400E] mb-2">Importante</p>
          <p className="text-[#92400E]">
            El titular de esta herramienta no se hace responsable de las decisiones tomadas en base a los calculos proporcionados ni de las consecuencias derivadas de su uso.
          </p>
        </div>
        <ul className="list-disc pl-6 space-y-2">
          <li>Los calculos son meramente orientativos y no vinculantes</li>
          <li>Las estimaciones de tiempo son aproximadas</li>
          <li>El usuario asume la responsabilidad total de sus decisiones profesionales</li>
          <li>No nos responsabilizamos de errores en la informacion generada por IA</li>
        </ul>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          7. Propiedad intelectual
        </h2>
        <p>
          Todos los contenidos de esta aplicacion (codigo, diseno, textos, imagenes) son propiedad de Jose Luis Vilaplana (Tecnoabogado) 
          y estan protegidos por la legislacion de propiedad intelectual. Queda prohibida su reproduccion sin autorizacion expresa.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          8. Legislacion aplicable
        </h2>
        <p>
          Este aviso legal se rige por la legislacion espanola y de la Union Europea. 
          Para cualquier controversia derivada del uso de esta herramienta, las partes se someten a los juzgados y tribunales correspondientes al domicilio del titular.
        </p>
      </section>
      
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          9. Documentos relacionados
        </h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><a href="/privacidad" className="text-[#8B1A1A] hover:underline">Politica de Privacidad</a></li>
          <li><a href="/cookies" className="text-[#8B1A1A] hover:underline">Politica de Cookies</a></li>
          <li><a href="/terminos" className="text-[#8B1A1A] hover:underline">Terminos de Uso</a></li>
          <li><a href="/uso-ia" className="text-[#8B1A1A] hover:underline">Transparencia IA (EU AI Act)</a></li>
        </ul>
      </section>
    </div>
  </LegalPageLayout>
);

export { LegalPageLayout };
